/**
 * Adaptador entre SheetJS (.xlsx) y el formato de datos de Univer.
 *
 * Univer no incluye I/O de .xlsx en la versión community; usamos SheetJS
 * para parsear/serializar y este adaptador traduce entre ambos modelos.
 *
 * Cobertura actual (MVP):
 *   - Valores (string / number / boolean / date)
 *   - Fórmulas
 *   - Estilos básicos (negrita, cursiva, color de texto, alineación)
 *   - Merges (celdas combinadas)
 *   - Anchos de columna / alturas de fila
 *   - Múltiples hojas
 *
 * Limitaciones conocidas (iteraremos):
 *   - No copia gráficos ni imágenes embebidas.
 *   - No preserva formato condicional ni validaciones complejas.
 *   - No preserva macros (los .xlsm se reducen a datos).
 */

import * as XLSX from 'xlsx';

/**
 * Convierte los bytes de un .xlsx a CSV plano de la primera hoja.
 * Re-usa SheetJS que ya tenemos cargado para el adaptador; evita re-importar.
 */
export function xlsxBytesToCsv(arrayBuffer) {
  const wb = XLSX.read(arrayBuffer, { type: 'array' });
  const firstSheetName = wb.SheetNames[0];
  const ws = wb.Sheets[firstSheetName];
  return XLSX.utils.sheet_to_csv(ws);
}

/* ------------------------------------------------------------------ */
/*  SheetJS workbook  ->  Univer WorkbookData                          */
/* ------------------------------------------------------------------ */

export function xlsxBytesToUniverWorkbookData(arrayBuffer, { name } = {}) {
  const wb = XLSX.read(arrayBuffer, { type: 'array', cellFormula: true, cellStyles: true, cellNF: true });
  return workbookToUniverData(wb, { name });
}

export function workbookToUniverData(wb, { name } = {}) {
  const sheets = {};
  const sheetOrder = [];

  wb.SheetNames.forEach((sheetName, idx) => {
    const ws = wb.Sheets[sheetName];
    const sheetId = `sheet-${idx + 1}`;
    sheetOrder.push(sheetId);

    const ref = ws['!ref'] ? XLSX.utils.decode_range(ws['!ref']) : { s: { c: 0, r: 0 }, e: { c: 0, r: 0 } };
    const rowCount = Math.max(100, ref.e.r + 5);
    const colCount = Math.max(26, ref.e.c + 5);

    // cellData: { rowIndex: { colIndex: { v, f, s? } } }
    const cellData = {};
    Object.keys(ws).forEach((addr) => {
      if (addr.startsWith('!')) return;
      const cell = ws[addr];
      const { c, r } = XLSX.utils.decode_cell(addr);
      if (!cellData[r]) cellData[r] = {};
      const entry = {};

      if (cell.f !== undefined) {
        entry.f = '=' + cell.f;
      }
      if (cell.v !== undefined) {
        // Univer espera el valor "crudo"; las fechas SheetJS vienen como número serial si w está presente
        entry.v = cell.v;
      }
      // Estilos básicos (SheetJS xlsx-style u hoja cellStyles)
      if (cell.s) {
        const s = {};
        if (cell.s.font) {
          if (cell.s.font.bold) s.bl = 1;
          if (cell.s.font.italic) s.it = 1;
          if (cell.s.font.color && cell.s.font.color.rgb) {
            s.cl = { rgb: '#' + cell.s.font.color.rgb.slice(-6) };
          }
          if (cell.s.font.sz) s.fs = cell.s.font.sz;
        }
        if (cell.s.alignment) {
          if (cell.s.alignment.horizontal) {
            s.ht = ({ left: 1, center: 2, right: 3 })[cell.s.alignment.horizontal] || undefined;
          }
          if (cell.s.alignment.vertical) {
            s.vt = ({ top: 1, middle: 2, bottom: 3 })[cell.s.alignment.vertical] || undefined;
          }
        }
        if (cell.s.fill && cell.s.fill.fgColor && cell.s.fill.fgColor.rgb) {
          s.bg = { rgb: '#' + cell.s.fill.fgColor.rgb.slice(-6) };
        }
        if (Object.keys(s).length) entry.s = s;
      }
      cellData[r][c] = entry;
    });

    // Merges
    const mergeData = (ws['!merges'] || []).map((m) => ({
      startRow: m.s.r,
      endRow: m.e.r,
      startColumn: m.s.c,
      endColumn: m.e.c,
    }));

    // Anchos de columna (SheetJS expone !cols con { wpx } o { wch })
    const columnData = {};
    (ws['!cols'] || []).forEach((col, i) => {
      if (!col) return;
      if (col.wpx) columnData[i] = { w: col.wpx };
      else if (col.wch) columnData[i] = { w: Math.round(col.wch * 7) };
    });

    // Alturas de fila
    const rowData = {};
    (ws['!rows'] || []).forEach((row, i) => {
      if (!row) return;
      if (row.hpx) rowData[i] = { h: row.hpx };
      else if (row.hpt) rowData[i] = { h: Math.round(row.hpt * 1.333) };
    });

    sheets[sheetId] = {
      id: sheetId,
      name: sheetName,
      rowCount,
      columnCount: colCount,
      cellData,
      mergeData,
      columnData,
      rowData,
    };
  });

  return {
    id: 'workbook-' + Date.now(),
    name: name || 'Hoja de cálculo',
    appVersion: '1.0.0',
    locale: 'es-ES',
    sheets,
    sheetOrder,
  };
}

/* ------------------------------------------------------------------ */
/*  Univer WorkbookData  ->  SheetJS workbook  ->  bytes .xlsx         */
/* ------------------------------------------------------------------ */

export function univerWorkbookDataToXlsxBytes(univerData) {
  const wb = XLSX.utils.book_new();

  (univerData.sheetOrder || Object.keys(univerData.sheets)).forEach((sheetId) => {
    const sheet = univerData.sheets[sheetId];
    if (!sheet) return;

    const ws = {};
    let maxR = 0;
    let maxC = 0;

    Object.keys(sheet.cellData || {}).forEach((rStr) => {
      const r = +rStr;
      Object.keys(sheet.cellData[rStr]).forEach((cStr) => {
        const c = +cStr;
        const cell = sheet.cellData[rStr][cStr];
        if (cell == null) return;

        const addr = XLSX.utils.encode_cell({ r, c });
        const out = {};

        if (cell.f) {
          // Univer guarda la fórmula con "=" delante
          out.f = String(cell.f).replace(/^=/, '');
        }
        if (cell.v !== undefined && cell.v !== null) {
          out.v = cell.v;
          out.t = typeof cell.v === 'number' ? 'n'
                : typeof cell.v === 'boolean' ? 'b'
                : 's';
        } else if (cell.f) {
          out.t = 'n';
          out.v = 0;
        }

        if (cell.s) {
          const s = {};
          if (cell.s.bl || cell.s.it || cell.s.cl || cell.s.fs) {
            s.font = {};
            if (cell.s.bl) s.font.bold = true;
            if (cell.s.it) s.font.italic = true;
            if (cell.s.cl && cell.s.cl.rgb) s.font.color = { rgb: cell.s.cl.rgb.replace('#', '') };
            if (cell.s.fs) s.font.sz = cell.s.fs;
          }
          if (cell.s.ht || cell.s.vt) {
            s.alignment = {};
            if (cell.s.ht) s.alignment.horizontal = ['', 'left', 'center', 'right'][cell.s.ht];
            if (cell.s.vt) s.alignment.vertical = ['', 'top', 'middle', 'bottom'][cell.s.vt];
          }
          if (cell.s.bg && cell.s.bg.rgb) {
            s.fill = { fgColor: { rgb: cell.s.bg.rgb.replace('#', '') }, patternType: 'solid' };
          }
          if (Object.keys(s).length) out.s = s;
        }

        ws[addr] = out;
        if (r > maxR) maxR = r;
        if (c > maxC) maxC = c;
      });
    });

    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: maxR, c: maxC } });

    // Merges
    if (sheet.mergeData && sheet.mergeData.length) {
      ws['!merges'] = sheet.mergeData.map((m) => ({
        s: { r: m.startRow, c: m.startColumn },
        e: { r: m.endRow, c: m.endColumn },
      }));
    }

    // Columnas
    if (sheet.columnData) {
      const cols = [];
      Object.keys(sheet.columnData).forEach((cStr) => {
        const c = +cStr;
        cols[c] = { wpx: sheet.columnData[cStr].w };
      });
      if (cols.length) ws['!cols'] = cols;
    }

    // Filas
    if (sheet.rowData) {
      const rows = [];
      Object.keys(sheet.rowData).forEach((rStr) => {
        const r = +rStr;
        rows[r] = { hpx: sheet.rowData[rStr].h };
      });
      if (rows.length) ws['!rows'] = rows;
    }

    XLSX.utils.book_append_sheet(wb, ws, sheet.name || sheetId);
  });

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
  return out instanceof Uint8Array ? out : new Uint8Array(out);
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Crea un WorkbookData mínimo vacío para "nueva hoja de cálculo". */
export function createEmptyUniverWorkbookData() {
  return {
    id: 'workbook-' + Date.now(),
    name: 'Nueva hoja',
    appVersion: '1.0.0',
    locale: 'es-ES',
    sheets: {
      'sheet-1': {
        id: 'sheet-1',
        name: 'Hoja 1',
        rowCount: 100,
        columnCount: 26,
        cellData: {},
        mergeData: [],
        columnData: {},
        rowData: {},
      },
    },
    sheetOrder: ['sheet-1'],
  };
}

/** Serializa una hoja vacía a bytes .xlsx (para "crear nuevo archivo"). */
export function createEmptyXlsxBytes() {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([[]]);
  XLSX.utils.book_append_sheet(wb, ws, 'Hoja 1');
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return out instanceof Uint8Array ? out : new Uint8Array(out);
}
